import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, unwrapArray } from "@/lib/api";
import { initialsOf } from "@/components/patterns";
import { cn } from "@/lib/utils";

type Person = { id: string; first_name?: string; last_name?: string };
const fullName = (p: Person) => `${p.first_name || ""} ${p.last_name || ""}`.trim();

let cache: Person[] | null = null;
/** The people who can be mentioned: everyone active, loaded once */
function usePeople() {
  const [people, setPeople] = useState<Person[]>(cache || []);
  useEffect(() => {
    if (cache) return;
    api
      .get("/api/User/Assignable")
      .then((res) => {
        cache = unwrapArray<Person>(res);
        setPeople(cache);
      })
      .catch(() => undefined);
  }, []);
  return people;
}

/**
 * A note box where typing @ offers the people you can mention. Choosing one writes "@First Last"; whoever is
 * named that way in a saved note is notified.
 */
export function MentionTextarea({
  value,
  onChange,
  rows = 2,
  placeholder,
  className,
}: {
  value: string;
  onChange: (value: string) => void;
  rows?: number;
  placeholder?: string;
  className?: string;
}) {
  const people = usePeople();
  const ref = useRef<HTMLTextAreaElement>(null);
  const [query, setQuery] = useState<{ at: number; text: string } | null>(null);
  const [active, setActive] = useState(0);

  const matches = useMemo(() => {
    if (!query) return [];
    const q = query.text.toLowerCase();
    return people.filter((p) => fullName(p).toLowerCase().includes(q)).slice(0, 6);
  }, [people, query]);

  const detect = (text: string, caret: number) => {
    const before = text.slice(0, caret);
    const m = /(^|\s)@([A-Za-z]*(?: [A-Za-z]*)?)$/.exec(before);
    if (m) {
      setQuery({ at: caret - m[2]!.length - 1, text: m[2]! });
      setActive(0);
    } else setQuery(null);
  };

  const choose = (p: Person) => {
    if (!query) return;
    const caret = ref.current?.selectionStart ?? value.length;
    const next = `${value.slice(0, query.at)}@${fullName(p)} ${value.slice(caret)}`;
    onChange(next);
    setQuery(null);
    requestAnimationFrame(() => {
      const pos = query.at + fullName(p).length + 2;
      ref.current?.focus();
      ref.current?.setSelectionRange(pos, pos);
    });
  };

  return (
    <div className="relative">
      <textarea
        ref={ref}
        rows={rows}
        value={value}
        placeholder={placeholder}
        aria-autocomplete="list"
        onChange={(e) => {
          onChange(e.target.value);
          detect(e.target.value, e.target.selectionStart);
        }}
        onKeyDown={(e) => {
          if (!query || matches.length === 0) return;
          if (e.key === "ArrowDown") (e.preventDefault(), setActive((a) => (a + 1) % matches.length));
          else if (e.key === "ArrowUp") (e.preventDefault(), setActive((a) => (a - 1 + matches.length) % matches.length));
          else if (e.key === "Enter" || e.key === "Tab") (e.preventDefault(), choose(matches[active] as Person));
          else if (e.key === "Escape") setQuery(null);
        }}
        onBlur={() => setTimeout(() => setQuery(null), 120)}
        className={className}
      />
      {query && matches.length > 0 && (
        <ul role="listbox" aria-label="Mention someone" className="absolute left-0 top-full z-30 mt-1 w-64 overflow-hidden rounded-lg border border-border bg-card shadow-md">
          {matches.map((p, i) => (
            <li key={p.id} role="option" aria-selected={i === active}>
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  choose(p);
                }}
                className={cn("flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-[13px]", i === active ? "bg-accent" : "hover:bg-muted")}
              >
                <span className="flex size-5 items-center justify-center rounded-full bg-accent text-[9px] font-bold text-accent-foreground">{initialsOf(fullName(p))}</span>
                {fullName(p)}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
